import { useEffect, useState } from "react";
import { Loader2, Check, Zap, Video, TrendingUp, ArrowRight, CheckCircle2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { LOGO } from "@/components/site/assets";

const inputCls = "w-full bg-[#121212] border border-white/10 focus:border-white outline-none px-4 py-3 text-sm transition-colors";
const labelCls = "font-mono-accent text-[10px] tracking-[0.2em] uppercase text-white/40 block mb-2";

const PERKS = [
  { icon: Zap, text: "Custom weekly speed & power programming" },
  { icon: Video, text: "Upload film — get coach video breakdowns" },
  { icon: TrendingUp, text: "Track 10-yard, flying 10, vertical & broad over time" },
];

export default function TrainOnline() {
  const [plans, setPlans] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", age: "", sport: "", position: "", school: "", height: "", weight: "", training_experience: "", injuries: "", prs: "", goals: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/public/digital-plans").then(({ data }) => {
      setPlans(data);
      if (data[0]) setSelected(data[0].id);
    }).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setSubmitting(true);
    const plan = plans.find((p) => p.id === selected);
    try {
      const { data } = await api.post("/digital/checkout", {
        ...form,
        plan_id: plan?.id || null,
        plan_name: plan?.name || "Online Coaching Membership",
        price: plan?.price ?? 200,
        interval: plan?.interval || "monthly",
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-6" data-testid="train-online-success">
        <div className="max-w-md text-center">
          <CheckCircle2 size={48} className="mx-auto text-white mb-6" />
          <h1 className="font-display font-black uppercase tracking-tighter text-3xl mb-4">You're In</h1>
          <p className="text-white/60 mb-6">Your Digital Athlete account is created. Check <span className="text-white">{done.email}</span> to set your password and start training.</p>
          <a href={done.activation_link} data-testid="train-online-activate-link" className="inline-flex items-center gap-2 bg-white text-black font-bold uppercase tracking-widest text-sm px-8 py-4 hover:bg-white/85 transition-colors">
            Activate Account <ArrowRight size={16} />
          </a>
          <p className="text-white/30 text-xs mt-6">Didn't get the email? Use the button above to activate now.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <header className="border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3"><img src={LOGO} alt="JJ Speed Factory" className="h-10 w-auto object-contain" /></a>
          <a href="/login" className="font-mono-accent text-xs tracking-[0.2em] uppercase text-white/60 hover:text-white transition-colors">Login</a>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-6 md:px-12 pt-16 pb-10">
        <p className="font-mono-accent text-xs tracking-[0.35em] uppercase text-white/40 mb-4">Online Coaching · No Evaluation Required</p>
        <h1 className="font-display font-black uppercase tracking-tighter text-4xl sm:text-5xl lg:text-6xl mb-6 max-w-3xl">Get Faster From Anywhere</h1>
        <p className="text-white/60 text-lg max-w-xl mb-10">Remote speed development built on the same system we use in person. Purchase a Online Coaching Membership and start today.</p>
        <div className="grid sm:grid-cols-3 gap-px bg-white/10 border border-white/10 max-w-3xl">
          {PERKS.map((p) => { const Icon = p.icon; return (
            <div key={p.text} className="bg-[#0A0A0A] p-6"><Icon size={20} className="text-white/70 mb-3" /><p className="text-white/70 text-sm">{p.text}</p></div>
          ); })}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 md:px-12 pb-24 grid lg:grid-cols-5 gap-10">
        <div className="lg:col-span-2">
          <h2 className="font-display font-bold uppercase tracking-tight text-xl mb-4">Choose Your Plan</h2>
          <div className="space-y-3" data-testid="digital-plans">
            {plans.length === 0 ? (
              <div className="border border-white/10 bg-[#121212] p-6" data-testid="digital-plan-default">
                <p className="font-display font-bold uppercase text-2xl">Online Coaching Membership</p>
                <p className="font-mono-accent text-3xl font-bold mt-2">$200<span className="text-white/40 text-sm">/mo</span></p>
              </div>
            ) : plans.map((p) => (
              <button key={p.id} type="button" data-testid={`digital-plan-${p.id}`} onClick={() => setSelected(p.id)}
                className={`w-full text-left p-5 border transition-colors ${selected === p.id ? "border-white bg-white/5" : "border-white/10 hover:border-white/25"}`}>
                <div className="flex items-center justify-between">
                  <p className="font-display font-bold uppercase text-xl">{p.name}</p>
                  {selected === p.id && <Check size={18} />}
                </div>
                <p className="font-mono-accent text-2xl font-bold mt-1">${p.price}<span className="text-white/40 text-sm">/{p.interval}</span></p>
                {p.description && <p className="text-white/50 text-sm mt-2">{p.description}</p>}
              </button>
            ))}
          </div>
          <p className="text-white/30 text-xs mt-4">Secure checkout powered by Stripe · cancel anytime. Test card 4242 4242 4242 4242.</p>
        </div>

        <form onSubmit={submit} className="lg:col-span-3 bg-[#121212] border border-white/10 p-6 md:p-8" data-testid="digital-onboarding-form">
          <h2 className="font-display font-bold uppercase tracking-tight text-xl mb-6">Athlete Onboarding</h2>
          <div className="grid sm:grid-cols-2 gap-5">
            <div><label className={labelCls}>First Name</label><input data-testid="do-first" required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className={inputCls} /></div>
            <div><label className={labelCls}>Last Name</label><input data-testid="do-last" required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className={inputCls} /></div>
            <div className="sm:col-span-2"><label className={labelCls}>Email</label><input data-testid="do-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} /></div>
            <div><label className={labelCls}>Age</label><input data-testid="do-age" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} className={inputCls} /></div>
            <div><label className={labelCls}>Sport</label><input data-testid="do-sport" value={form.sport} onChange={(e) => setForm({ ...form, sport: e.target.value })} className={inputCls} /></div>
            <div><label className={labelCls}>Position / Event</label><input value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} className={inputCls} /></div>
            <div><label className={labelCls}>School</label><input value={form.school} onChange={(e) => setForm({ ...form, school: e.target.value })} className={inputCls} /></div>
            <div><label className={labelCls}>Height</label><input value={form.height} onChange={(e) => setForm({ ...form, height: e.target.value })} className={inputCls} /></div>
            <div><label className={labelCls}>Weight</label><input value={form.weight} onChange={(e) => setForm({ ...form, weight: e.target.value })} className={inputCls} /></div>
            <div className="sm:col-span-2"><label className={labelCls}>Training Experience</label><input value={form.training_experience} onChange={(e) => setForm({ ...form, training_experience: e.target.value })} className={inputCls} /></div>
            <div className="sm:col-span-2"><label className={labelCls}>Current Injuries</label><input value={form.injuries} onChange={(e) => setForm({ ...form, injuries: e.target.value })} className={inputCls} /></div>
            <div className="sm:col-span-2"><label className={labelCls}>Current PRs</label><input value={form.prs} onChange={(e) => setForm({ ...form, prs: e.target.value })} className={inputCls} /></div>
            <div className="sm:col-span-2"><label className={labelCls}>Goals</label><textarea rows={3} value={form.goals} onChange={(e) => setForm({ ...form, goals: e.target.value })} className={inputCls} /></div>
          </div>
          {error && <p data-testid="do-error" className="text-sm text-red-400 border border-red-400/30 bg-red-400/5 px-4 py-3 mt-5">{error}</p>}
          <p className="text-white/40 text-xs mt-5">You'll upload sprint / broad / vertical videos from your dashboard after activation.</p>
          <button data-testid="do-submit" disabled={submitting} className="w-full mt-6 inline-flex items-center justify-center gap-2 bg-white text-black font-bold uppercase tracking-widest text-sm py-4 hover:bg-white/85 transition-colors disabled:opacity-60">
            {submitting ? <Loader2 size={16} className="animate-spin" /> : <Zap size={16} />} {submitting ? "Redirecting to Checkout..." : "Continue to Payment"}
          </button>
        </form>
      </section>
    </div>
  );
}
